import { Injectable } from '@angular/core';
import * as dayjs from 'dayjs';
import { Subject } from 'rxjs';
import { RepositoryService } from '../baseServices/repository.service';
import { ActivityConstants, ActivityType, ClockActivityText } from '../constant/activity-constants';
import { GlobalConstants } from '../constant/constant';
import { BaseActivityDataDto, ClockActivityDataDto, CreateClockActivityDataDto, CreateFlexDocActivityDataDto, FlexDocActivityDataDto, UpdateActivityForeignKeysDataDto } from '../dtos/activity-service-dtos';
import { CreateNotableActivityDataDto, NotableActivityDataDto } from '../dtos/activity-service-dtos/data-dtos/notable-activity-data-dto';
import { Activity } from '../entities/Activity';
import { StoreName } from '../entities/StoreName';
import { DataChangeEmitPayload, DataChangeEmitWhat, DataChangeEmitWho } from '../models/data-change-emit/data-change-emit-payload';
import { DbKeyType } from '../models/db/db-store';
import { DbTransaction } from '../models/db/db-transaction';
import { Callback, CallbackAddMode, CallbackNames } from '../models/db/db-transaction-callback';
import { DateTimeHelper, DateTimeType } from '../utils/date-time-helper';
import { Check, EntityHelper } from '../utils/entity-helper';
import { ConflictValidationException, RecordNotFoundException, RecordSoftDeletedException, StateValidationException, ValidationException } from '../utils/exceptions';
import { NotificationsService } from './notifications.service';
import { LOCAL_NOTIFICATION_ACTION_TYPE } from '../enums/notification/notification.enum';
import { EntityTypeService } from './entity-type.service';

@Injectable({
  providedIn: 'root',
})
export class ActivityService {
  private emitChangeSource = new Subject<DataChangeEmitPayload<Activity>>();
  changeEmitted$ = this.emitChangeSource.asObservable();
  constructor(private repository: RepositoryService, private entityTypeService: EntityTypeService, private notificationsService: NotificationsService) {}

  // #endregion
  // #region Utility

  emitChange(activity: Activity, who: DataChangeEmitWho, what: DataChangeEmitWhat) {
    this.emitChangeSource.next({ data: activity, who: who, what: what });
  }

  getEmitChangeCallback(activity: Activity, who: DataChangeEmitWho, what: DataChangeEmitWhat): Callback {
    return {
      name: CallbackNames.EmitChangeForActivity + '_' + activity.RecordId,
      fn: this.emitChange.bind(this, activity, who, what),
    };
  }

  private getValidateActivityCallback(activity: Activity): Callback {
    return {
      name: CallbackNames.ValidateActivity,
      fn: this.validateActivity.bind(this, activity),
    };
  }

  getScheduleReminderCallback(): Callback {
    return {
      name: CallbackNames.ScheduleActivityReminder,
      fn: () => {
        const time = dayjs().format('h:mm A');
        this.notificationsService.scheduleLocalNotification({
          id: LOCAL_NOTIFICATION_ACTION_TYPE.ACTIVITY_LOG,
          title: 'Reminder',
          body: `You have not entered an activity into your activity log since ${time}. Tap here to enter an activity log entry now.`,
          ongoing: true,
          schedule: { every: 'hour', count: 2, allowWhileIdle: true },
        });
      },
    };
  }

  private async startTransactionIfNeeded(existingTransaction?: DbTransaction, additionalStoreNames?: StoreName[], mode?: 'readwrite' | 'readonly'): Promise<DbTransaction> {
    if (existingTransaction) {
      return existingTransaction;
    } else {
      const storeNames = additionalStoreNames ? [StoreName.Activity, StoreName.EntityType, ...additionalStoreNames] : [StoreName.Activity, StoreName.EntityType];
      return await this.repository.startTransaction(Array.from(new Set(storeNames)), mode ?? 'readwrite');
    }
  }

  // #endregion
  // #region Low-level Get One

  async getActivityByRecordId(recordId: number, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<Activity> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const result = await this.repository.getByLocalId<Activity>(StoreName.Activity, recordId, Activity, transaction);

    if (result === null) {
      throw new RecordNotFoundException(recordId);
    }

    const record = result as Activity;

    if (this.checkIsActivitySoftDeleted(record) && !includeDeleted) {
      throw new RecordSoftDeletedException(recordId);
    }

    return record;
  }

  async getActivityByOnlineId(onlineId: number, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<Activity> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const result = await this.repository.getByOnlineId<Activity>(StoreName.Activity, onlineId, Activity, transaction);

    if (result === null) {
      throw new RecordNotFoundException(onlineId);
    }

    const record = result as Activity;

    if (this.checkIsActivitySoftDeleted(record) && !includeDeleted) {
      throw new RecordSoftDeletedException(onlineId);
    }

    return record;
  }

  // #endregion
  // #region Low-level Get Many

  async getActivitiesByActivityLogId(activityLogId: number, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<Activity[]> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const result = await this.repository.getAllByIndex<Activity>(StoreName.Activity, GlobalConstants.KEY_NAMES.ActivityLogId, activityLogId, Activity, transaction);
    let records = result as Activity[];

    if (!includeDeleted) {
      records = records.filter(record => !this.checkIsActivitySoftDeleted(record));
    }

    return records;
  }

  async getActivitiesByActivityLogRecordId(activityLogRecordId: number, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<Activity[]> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const result = await this.repository.getAllByIndex<Activity>(StoreName.Activity, GlobalConstants.KEY_NAMES.ActivityLogRecordId, activityLogRecordId, Activity, transaction);
    let records = result as Activity[];

    if (!includeDeleted) {
      records = records.filter(record => !this.checkIsActivitySoftDeleted(record));
    }

    return records;
  }

  async getActivitiesByReferenceId(referenceId: number, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<Activity[]> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const result = await this.repository.getAllByIndex<Activity>(StoreName.Activity, GlobalConstants.KEY_NAMES.TimeRecordId, referenceId, Activity, transaction);
    let records = result as Activity[];

    if (!includeDeleted) {
      records = records.filter(record => !this.checkIsActivitySoftDeleted(record));
    }

    return records;
  }

  async getActivitiesByReferenceRecordId(referenceRecordId: number, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<Activity[]> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const result = await this.repository.getAllByIndex<Activity>(StoreName.Activity, GlobalConstants.KEY_NAMES.TimeRecordRecordId, referenceRecordId, Activity, transaction);
    let records = result as Activity[];

    if (!includeDeleted) {
      records = records.filter(record => !this.checkIsActivitySoftDeleted(record));
    }

    return records;
  }

  async getActivitiesByUserIds(userIds: number[], existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<Activity[]> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const result = await this.repository.getAllByIndex(StoreName.Activity, GlobalConstants.KEY_NAMES.UserId, userIds, Activity, transaction);
    let records = result as Activity[];

    if (!includeDeleted) {
      records = records.filter(record => !this.checkIsActivitySoftDeleted(record));
    }

    return records;
  }

  async getActivitiesByUserIdDate(userId: number, date: DateTimeType, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<Activity[]> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const startDate = DateTimeHelper.formatDateTime(DateTimeHelper.getStartOfDay(date));
    const endDate = DateTimeHelper.formatDateTime(DateTimeHelper.getEndOfDay(date));
    const key: DbKeyType = {
      lower: [userId, startDate],
      upper: [userId, endDate],
    };

    const result = await this.repository.getAllByIndex(StoreName.Activity, GlobalConstants.KEY_NAMES.UserId_Date, key, Activity, transaction);
    let records = result as Activity[];

    if (!includeDeleted) {
      records = result.filter(Activity => !this.checkIsActivitySoftDeleted(Activity));
    }

    return records;
  }

  // #endregion
  // #region Low-level Create

  async createActivity(who: DataChangeEmitWho, recordToCreate: Activity, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<number> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    if (who === DataChangeEmitWho.Self) {
      // LC1. Validate action
      await this.validateActivityCreation(transaction);

      // LC2. Idempotent check
      const existingRecord = (await this.getActivitiesByUserIdDate(recordToCreate.UserId, recordToCreate.UserSpecifiedDate, transaction)).find(existingRecord =>
        this.checkCoreFieldsMatch(recordToCreate, existingRecord)
      );
      if (existingRecord != null) {
        return existingRecord.RecordId;
      }

      // LC3. Validate model
      await this.validateActivity(recordToCreate, transaction);
    }
    else {
      transaction.addOnCommitCallback(this.getValidateActivityCallback(recordToCreate), CallbackAddMode.PushDuplicate);
    }

    // LC4. Repository call
    const recordId = await this.repository.create(recordToCreate, StoreName.Activity, transaction);

    // LC5. Get created record
    const createdRecord = await this.getActivityByRecordId(recordId, transaction, includeDeleted);

    // LC6. Report changes
    transaction.addOnCommitCallback(this.getEmitChangeCallback(createdRecord, who, DataChangeEmitWhat.Create));

    // LC7. Return id
    return recordId;
  }

  // #endregion
  // #region Low-level Update

  async updateActivity(who: DataChangeEmitWho, recordToUpdate: Activity, skipLocalCopyValidation: boolean, localCopy: Activity | null, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    if (who === DataChangeEmitWho.Self) {
      // LU1. Get existing record
      const existingRecord = await this.getActivityByRecordId(recordToUpdate.RecordId, transaction);

      // LU2. Validate action
      await this.validateActivityUpdate(recordToUpdate, transaction);

      // LU3. Idempotent check
      if (EntityHelper.checkAllFieldsEqual(existingRecord, recordToUpdate)) {
        return;
      }

      // LU4. Validate local copy
      if (!skipLocalCopyValidation) {
        const checks: Check<Activity>[] = [{ name: 'ImmutableFields', function: this.checkImmutableFieldsMatch }];
        EntityHelper.validateLocalCopy<Activity>(localCopy, existingRecord, checks);
      }

      // LU5. Validate model
      await this.validateActivity(recordToUpdate, transaction);
    }
    else {
      transaction.addOnCommitCallback(this.getValidateActivityCallback(recordToUpdate), CallbackAddMode.PushDuplicate);
    }

    // LU6. Repository call
    await this.repository.update(recordToUpdate, StoreName.Activity, transaction);

    // LU7. Get updated record
    const updatedRecord = await this.getActivityByRecordId(recordToUpdate.RecordId, transaction, true);

    // LU8. Report changes
    transaction.addOnCommitCallback(this.getEmitChangeCallback(updatedRecord, who, DataChangeEmitWhat.Update));

    // LU9. Return
    return;
  }

  // #endregion
  // #region Low-level Delete

  async softDeleteActivity(who: DataChangeEmitWho, recordId: number, skipLocalCopyValidation: boolean, localCopy: Activity | null, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // LD1. Get existing record
    const recordToSoftDelete = await this.getActivityByRecordId(recordId, transaction, true);

    // LD2. Validate action
    await this.validateActivitySoftDeletion(recordToSoftDelete, transaction);

    // LD3. Idempotent check
    if (this.checkIsActivitySoftDeleted(recordToSoftDelete)) {
      return;
    }

    // LD4. Validate local copy
    if (!skipLocalCopyValidation) {
      const checks: Check<Activity>[] = [{ name: 'ImmutableFields', function: this.checkImmutableFieldsMatch }];
      EntityHelper.validateLocalCopy(localCopy, recordToSoftDelete, checks);
    }

    // LD5. Prepare changes
    recordToSoftDelete.setSoftDeleted();

    // LD6. Validate model
    await this.validateSoftDeletedActivity(recordToSoftDelete, transaction);

    // LD7. Repository call
    await this.repository.update(recordToSoftDelete, StoreName.Activity, transaction);

    // LD8. Get soft deleted record
    const softDeletedRecord = await this.getActivityByRecordId(recordId, transaction, true);

    // LD9. Report changes
    transaction.addOnCommitCallback(this.getEmitChangeCallback(softDeletedRecord, who, DataChangeEmitWhat.Update));

    // LD10. Return
    return;
  }

  public async hardDeleteActivity(who: DataChangeEmitWho, recordId: number, skipLocalCopyValidation: boolean, localCopy: Activity | null, existingTransaction?: DbTransaction) {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // 1. Idempotent check
    try {
      // 2. Get existing record
      const existingRecord = await this.getActivityByRecordId(recordId, transaction, true);

      // 3. Local copy validate
      if (!skipLocalCopyValidation) {
        const checks: Check<Activity>[] = [{ name: 'ImmutableFields', function: this.checkImmutableFieldsMatch }];
        EntityHelper.validateLocalCopy(localCopy, existingRecord, checks);
      }

      // 4. Validate hard delete
      await this.validateActivityHardDeletion(existingRecord, transaction);

      // 5. Repository call
      await this.repository.delete(recordId, StoreName.Activity, transaction);

      // 6. Report changes
      transaction.addOnCommitCallback(this.getEmitChangeCallback(existingRecord, who, DataChangeEmitWhat.Delete));

      // 7. Return
      return;
    } catch (e) {
      if (e instanceof RecordNotFoundException) {
        return;
      }
      throw e;
    }
  }

  // #endregion
  // #region Low-level Validate Action

  async validateActivityCreation(existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // TODO: Check role claim

    return;
  }

  async validateActivityUpdate(existingRecord: Activity, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // TODO: Check role claim

    return;
  }

  async validateActivitySoftDeletion(existingRecord: Activity, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // TODO: Check role claim

    return;
  }

  async validateActivityHardDeletion(existingRecord: Activity, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);
    // TODO: Check role claim
    // Add other validation checks as necessary
  }

  // #endregion
  // #region Low-level Validate Model

  async validateActivity(activity: Activity, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const isActivitySoftDeleted = this.checkIsActivitySoftDeleted(activity);
    if (isActivitySoftDeleted) {
      return;
    } else {
      const duplicateActivity = await this.getDuplicateActivity(activity, transaction);
      if (duplicateActivity != null) {
        throw new ConflictValidationException(duplicateActivity);
      }
    }

    return;
  }

  async validateSoftDeletedActivity(activity: Activity, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    if (!this.checkIsActivitySoftDeleted(activity)) {
      throw new StateValidationException('SoftDeleted', activity);
    }

    return;
  }

  // #endregion
  // #region Low-level Sync

  // #endregion
  // #region Mid-level Get One

  async getDuplicateActivity(targetActivity: Activity, existingTransaction?: DbTransaction): Promise<Activity> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const activitiesForUserAndDate = await this.getActivitiesByUserIdDate(targetActivity.UserId, targetActivity.UserSpecifiedDate, transaction);

    const duplicateActivity = activitiesForUserAndDate.find(existingActivity =>
      !this.checkAnyActivityIdentifiersMatch(targetActivity, existingActivity)
      && this.checkDuplicateMatch(targetActivity, existingActivity)
    );

    return duplicateActivity;
  }

  async getNotableActivitiesByUserIdDate(userId: number, date: DateTimeType, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<Activity[]> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);
    const allActivities = await this.getActivitiesByUserIdDate(userId, date, transaction, includeDeleted);
    const notableActivities = allActivities.filter(activity => activity.Type === ActivityType.NotableActivity);
    return notableActivities;
  }

  // #endregion
  // #region Mid-level Get Many

  // #endregion
  // #region Mid-level Create

  async createClockActivity(who: DataChangeEmitWho, data: CreateClockActivityDataDto, existingTransaction?: DbTransaction): Promise<number> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // MC1. Validate action
    await this.validateClockActivityCreation(data, transaction);

    // MC2. Build model
    const recordToCreate = await this.buildClockActivityDto(data, data, null, transaction);

    // MC3. Idempotent check
    const existingRecord = await this.getDuplicateActivity(recordToCreate, transaction);
    if (existingRecord) {
      return existingRecord.RecordId;
    }

    // MC4. Low-level create
    const recordId = await this.createActivity(who, recordToCreate, transaction);

    // MC5. Return id
    return recordId;
  }

  async createNotableActivity(who: DataChangeEmitWho, data: CreateNotableActivityDataDto, existingTransaction?: DbTransaction): Promise<number> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // MC1. Validate action
    await this.validateNotableActivityCreation(data, transaction);

    // MC2. Build model
    const recordToCreate = await this.buildNotableActivityDto(data, data, null, transaction);

    // MC3. Idempotent check
    const existingRecord = await this.getDuplicateActivity(recordToCreate, transaction);
    if (existingRecord) {
      return existingRecord.RecordId;
    }

    // MC4. Low-level create
    const recordId = await this.createActivity(who, recordToCreate, transaction);

    // MC5. Return id
    return recordId;
  }

  async createFlexDocActivity(who: DataChangeEmitWho, data: CreateFlexDocActivityDataDto, existingTransaction?: DbTransaction): Promise<number> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // MC1. Validate action
    await this.validateFlexDocActivityCreation(data, transaction);

    // MC2. Build model
    const recordToCreate = await this.buildFlexDocActivityDto(data, data, null, transaction);

    // MC3. Idempotent check
    const existingRecord = await this.getDuplicateActivity(recordToCreate, transaction);
    if (existingRecord) {
      return existingRecord.RecordId;
    }

    // MC4. Low-level create
    const recordId = await this.createActivity(who, recordToCreate, transaction);

    // MC5. Return id
    return recordId;
  }

  // #endregion
  // #region Mid-level Update

  async updateActivityForeignKeys(who: DataChangeEmitWho, recordId: number, data: UpdateActivityForeignKeysDataDto, skipLocalCopyValidation: boolean, localCopy: Activity | null, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // MU1. Get existing record
    const existingRecord = await this.getActivityByRecordId(recordId, transaction);

    // MU2. Validate action
    await this.validateActivityForeignKeysUpdate(data, transaction);

    // MU3. Prepare changes
    const recordToUpdate = existingRecord.clone();
    recordToUpdate.setForeignKeys(data.ActivityLogRecordId, data.TimeRecordRecordId, recordToUpdate.EntityTypeId, data.ActivityLogId, data.TimeRecordId);

    // MU4. Idempotent check
    if (this.checkCoreFieldsMatch(existingRecord, recordToUpdate)) {
      return;
    }

    // MU5. Validate local copy
    if (!skipLocalCopyValidation) {
      const checks: Check<Activity>[] = [
        { name: "ImmutableFields", function: this.checkImmutableFieldsMatch },
      ];
      EntityHelper.validateLocalCopy<Activity>(localCopy, existingRecord, checks);
    }

    // MU6. Low-level update
    await this.updateActivity(who, recordToUpdate, skipLocalCopyValidation, localCopy, transaction);

    // MU7. Return
    return;
  }

  async updateClockActivity(who: DataChangeEmitWho, recordId: number, data: ClockActivityDataDto, skipLocalCopyValidation: boolean, localCopy: Activity | null, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // MU1. Get existing record
    const existingRecord = await this.getActivityByRecordId(recordId, transaction);

    // MU2. Validate action
    await this.validateClockActivityUpdate(data, existingRecord, true, transaction);

    // MU3. Prepare changes
    const recordToUpdate = await this.buildClockActivityDto(data, null, existingRecord, transaction);

    // MU4. Idempotent check
    if (this.checkCoreFieldsMatch(existingRecord, recordToUpdate)) {
      return;
    }

    // MU5. Validate local copy
    if (!skipLocalCopyValidation) {
      const checks: Check<Activity>[] = [{ name: 'ImmutableFields', function: this.checkImmutableFieldsMatch }];
      EntityHelper.validateLocalCopy<Activity>(localCopy, existingRecord, checks);
    }

    // MU6. Low-level update
    await this.updateActivity(who, recordToUpdate, skipLocalCopyValidation, localCopy, transaction);

    // MU7. Return
    return;
  }

  async updateNotableActivity(
    who: DataChangeEmitWho,
    recordId: number,
    data: NotableActivityDataDto,
    skipLocalCopyValidation: boolean,
    localCopy: Activity | null,
    existingTransaction?: DbTransaction
  ): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // MU1. Get existing record
    const existingRecord = await this.getActivityByRecordId(recordId, transaction);

    // MU2. Validate action
    await this.validateNotableActivityUpdate(data, existingRecord, true, transaction);

    // MU3. Prepare changes
    const recordToUpdate = await this.buildNotableActivityDto(data, null, existingRecord, transaction);

    // MU4. Idempotent check
    if (this.checkCoreFieldsMatch(existingRecord, recordToUpdate)) {
      return;
    }

    // MU5. Validate local copy
    if (!skipLocalCopyValidation) {
      const checks: Check<Activity>[] = [{ name: 'ImmutableFields', function: this.checkImmutableFieldsMatch }];
      EntityHelper.validateLocalCopy<Activity>(localCopy, existingRecord, checks);
    }

    // MU6. Low-level update
    await this.updateActivity(who, recordToUpdate, skipLocalCopyValidation, localCopy, transaction);

    // MU7. Return
    return;
  }

  // #endregion
  // #region Mid-level Delete

  // #endregion
  // #region Mid-level Validate Action

  async validateActivityForeignKeysUpdate(data: UpdateActivityForeignKeysDataDto, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);
    // TODO: Check role claim
  }

  async validateClockActivityCreation(data: CreateClockActivityDataDto, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);
    // TODO: Check role claim
  }

  async validateClockActivityUpdate(data: ClockActivityDataDto, existingRecord: Activity, skipLowLevelValidation: boolean, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);
    // TODO: Check role claim
  }

  async validateNotableActivityCreation(data: CreateNotableActivityDataDto, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);
    // TODO: Check role claim
  }

  async validateNotableActivityUpdate(data: NotableActivityDataDto, existingRecord: Activity, skipLowLevelValidation: boolean, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);
    // TODO: Check role claim
  }

  async validateFlexDocActivityCreation(data: CreateFlexDocActivityDataDto, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);
    // TODO: Check role claim
  }

  // #endregion
  // #region Mid-level Validate Model

  async validateClockActivity(activity: Activity, skipLowLevelValidation: boolean = false, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    if (!skipLowLevelValidation) {
      await this.validateActivity(activity, transaction);
    }

    if (!this.checkIsClockActivity(activity)) {
      throw new ValidationException('Activity is not a clock activity.');
    }
  }

  async validateNotableActivity(activity: Activity, skipLowLevelValidation: boolean = false, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    if (!skipLowLevelValidation) {
      await this.validateActivity(activity, transaction);
    }

    if (!this.checkIsNotableActivity(activity)) {
      throw new ValidationException('Activity is not a notable activity.');
    }
  }

  async validateFlexDocActivity(activity: Activity, skipLowLevelValidation: boolean = false, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    if (!skipLowLevelValidation) {
      await this.validateActivity(activity, transaction);
    }

    if (!this.checkIsFlexDocActivity(activity)) {
      throw new ValidationException('Activity is not a Flex document activity.');
    }
  }


  // #endregion
  // #region Mid-level Upsert

  // #endregion
  // #region Mid-level Sync

  async updateActivityWithId(who: DataChangeEmitWho, recordId: number, onlineId: number, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    if (who !== DataChangeEmitWho.Sync) {
      throw new Error('Must originate from Sync.');
    }

    let recordToUpdate = await this.getActivityByRecordId(recordId, transaction, true);
    recordToUpdate.Id = onlineId;

    await this.updateActivity(who, recordToUpdate, true, null, transaction);
  }

  async upsertActivityFromSync(who: DataChangeEmitWho, recordToUpsert: Activity, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    if (who !== DataChangeEmitWho.Sync) {
      throw new Error('Must originate from Sync.');
    }

    try {
      const existingRecord = await this.getActivityByOnlineId(recordToUpsert.Id, transaction, true);
      recordToUpsert.RecordId = existingRecord.RecordId;
      await this.updateActivity(who, recordToUpsert, true, null, transaction);
    } catch (e) {
      if (e instanceof RecordNotFoundException) {
        await this.createActivity(who, recordToUpsert, transaction, true);
      }
    }

    return;
  }

  // #endregion
  // #region Build

  private async buildBaseActivityDto(data: BaseActivityDataDto, existingTransaction?: DbTransaction): Promise<Activity> {
    return new Activity({
      UserId: data.UserId,
      Type: data.Type,
      AttachCount: 0,
      IsDeleted: false,
      IsArchived: false,
    });
  }

  private async buildClockActivityDto(
    data: ClockActivityDataDto,
    baseData: CreateClockActivityDataDto | null,
    existingRecord: Activity | null,
    existingTransaction?: DbTransaction
  ): Promise<Activity> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    if (baseData === null && existingRecord === null) {
      throw new Error('data and existingRecord cannot both be null.');
    }

    const record = existingRecord?.clone() ?? (await this.buildBaseActivityDto({ UserId: baseData.UserId, Type: baseData.Type } as BaseActivityDataDto, transaction));

    const text = ClockActivityText[record.Type];
    const timeRecordEntityTypeId = await this.getTimeRecordEntityType(transaction);
    record.setText(text);
    record.setForeignKeys(data.ActivityLogRecordId, data.TimeRecordRecordId, timeRecordEntityTypeId, data.ActivityLogId, data.TimeRecordId);
    record.setCoordinates(data.Coordinates.Longitude, data.Coordinates.Latitude);
    record.setActivityDate(data.ActivityDate, data.TimeZoneId);

    await this.validateClockActivity(record, true, transaction);

    return record;
  }

  private async buildNotableActivityDto(
    data: NotableActivityDataDto,
    baseData: CreateNotableActivityDataDto | null,
    existingRecord: Activity | null,
    existingTransaction?: DbTransaction
  ): Promise<Activity> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    if (baseData === null && existingRecord === null) {
      throw new Error('data and existingRecord cannot both be null.');
    }

    const record = existingRecord?.clone() ?? (await this.buildBaseActivityDto({ UserId: baseData.UserId, Type: ActivityType.NotableActivity } as BaseActivityDataDto, transaction));

    const timeRecordEntityTypeId = await this.getTimeRecordEntityType(transaction);
    record.setText(data.Text);
    record.setForeignKeys(data.ActivityLogRecordId, data.TimeRecordRecordId, timeRecordEntityTypeId, data.ActivityLogId, data.TimeRecordId);
    record.setCoordinates(data.Coordinates.Longitude, data.Coordinates.Latitude);
    record.setActivityDate(data.ActivityDate, data.TimeZoneId);

    await this.validateNotableActivity(record, true, transaction);

    return record;
  }

  private async buildFlexDocActivityDto(
    data: FlexDocActivityDataDto,
    baseData: CreateFlexDocActivityDataDto | null,
    existingRecord: Activity | null,
    existingTransaction?: DbTransaction
  ): Promise<Activity> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    if (baseData === null && existingRecord === null) {
      throw new Error('data and existingRecord cannot both be null.');
    }

    const record = existingRecord?.clone() ?? (await this.buildBaseActivityDto({ UserId: baseData.UserId, Type: ActivityType.FlexDoc } as BaseActivityDataDto, transaction));

    const documentEntityTypeId = await this.getDocumentEntityType(transaction);
    const text = ActivityConstants.DOCUMENT_SUBMIT_ACTIVITYLOG_TEXT(data.FormName, data.DocumentNumber);
    record.setText(text);
    record.setForeignKeys(data.ActivityLogRecordId, null, documentEntityTypeId, data.ActivityLogId, null);
    record.setCoordinates(data.Coordinates.Longitude, data.Coordinates.Latitude);
    record.setActivityDate(data.ActivityDate, data.TimeZoneId);

    await this.validateFlexDocActivity(record, true, transaction);

    return record;
  }

  // #endregion
  // #region Check

  checkIsActivitySoftDeleted(activity: Activity): boolean {
    return activity.IsDeleted;
  }

  private checkImmutableFieldsMatch(a: Activity, b: Activity): boolean {
    return a.UserId === b.UserId && a.Type === b.Type;
  }

  private checkTextFieldsMatch(a: Activity, b: Activity): boolean {
    return a.Text === b.Text;
  }

  private checkTimeFieldsMatch(a: Activity, b: Activity): boolean {
    return a.UserSpecifiedDate === b.UserSpecifiedDate && a.TimeZoneId === b.TimeZoneId;
  }

  private checkForeignKeyFieldsMatch(a: Activity, b: Activity): boolean {
    return a.ActivityLogId === b.ActivityLogId && a.ReferenceId === b.ReferenceId && a.EntityTypeId === b.EntityTypeId;
  }

  private checkCoreFieldsMatch(a: Activity, b: Activity): boolean {
    return this.checkImmutableFieldsMatch(a, b) && this.checkTextFieldsMatch(a, b) && this.checkTimeFieldsMatch(a, b) && this.checkForeignKeyFieldsMatch(a, b);
  }

  private checkTimesMatch(a: Activity, b: Activity): boolean {
    return DateTimeHelper.checkTimeIsEqual(a.UserSpecifiedDate, a.TimeZoneId, b.UserSpecifiedDate, b.TimeZoneId);
  }

  private checkDuplicateMatch(a: Activity, b: Activity): boolean {
    return this.checkImmutableFieldsMatch(a, b) && this.checkTextFieldsMatch(a, b) && this.checkTimesMatch(a, b);
  }

  checkIsClockActivity(activity: Activity): boolean {
    return [ActivityType.ClockIn, ActivityType.ClockOut, ActivityType.Meal, ActivityType.Rest].includes(activity.Type);
  }

  checkIsNotableActivity(activity: Activity): boolean {
    return [ActivityType.NotableActivity].includes(activity.Type);
  }

  checkIsFlexDocActivity(activity: Activity): boolean {
    return [ActivityType.FlexDoc].includes(activity.Type);
  }

  checkAnyActivityIdentifiersMatch(a: Activity, b: Activity): boolean {
    if (!a?.RecordId && !a?.Id) {
      return false;
    }

    if (!b?.RecordId && !b?.Id) {
      return false;
    }

    return a?.RecordId === b?.RecordId || a?.Id === b?.Id;
  }

  // #endregion
  // #region Lookup

  private async getTimeRecordEntityType(existingTransaction?: DbTransaction): Promise<number> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);
    return await this.entityTypeService.getEntityTypeIdByNumber('TimeRecord', transaction);
  }

  private async getDocumentEntityType(existingTransaction?: DbTransaction): Promise<number> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);
    return await this.entityTypeService.getEntityTypeIdByNumber('Document', transaction);
  }

  // #endregion
  // #region Transform

  filterForUniqueActivities(activities: Activity[]): Activity[] {
    return activities.filter((e, i) => activities.findIndex(a => (a['Id'] && e['Id'] && a['Id'] === e['Id']) || a['RecordId'] === e['RecordId']) === i);
  }

  getActivityTimeZoneAbbreviation(activity: Activity): string {
    return DateTimeHelper.getTimeZoneAbbreviation(activity.UserSpecifiedDate, activity.TimeZoneId);
  }

  // #endregion
  // #region Other

  // #endregion
}
